import { FormLayoutProps } from 'src/app/layout/form-layout/form-layout.types'
import { NoteType, CreditType, RqQueryForResourceFilter, ManualDailyPriceInput, ReservationRatesQuery, BookingPolicyReservationFragment } from 'src/api/api.generated'

export type ReservationGroupUpsertFilter = {
  start?: Date
  end?: Date
  resourceTypes?: Record<string, number>[] // Pair of resourceTypeId and quantity
}

export type ReservationNewGroupFormData = {
  reservedResources: {
    cartRowId: string
    rateId: string
    policy?: BookingPolicyReservationFragment | null
    chargeCategoryId: string
    manualDailyPrices?: ManualDailyPriceInput[]
    resourceOccupancies: [
      {
        start: Date
        end: Date
        resourceTypeId: string
      },
    ]
    adults: number
    children: number
    childOccupancies:
      | {
          childId: string
          quantity: number
        }[]
      | null
    additionalServices?: {
      additionalServiceId: string
      quantity: number | undefined
    }[]
  }[]

  tenantId: string
  propertyId: string[]
  originId: string
  isGroup: boolean
  group: string
  reference: {
    mainGuestId: string
    customerCompanyId?: string | null
    customerPersonId?: string | null
  }
  reservationStateId: string
  optionExpiration?: Date
  notes:
    | [
        {
          type: NoteType
          description: string
        },
      ]
    | null
  credit?: CreditType
  disableAutomation?: boolean
}

export type ReservationGroupUpsertFormProps = {
  loading?: boolean
  reservedResourceId?: string
  propertyId: string
  formLayoutProps: Omit<FormLayoutProps, 'toolbarContents' | 'title' | 'toolbar'>
  onSubmitComplete: () => void
  onCancel: () => void
  onGoToCreateSingleReservation?: () => void
  onSubmitAndOpenReservation?: (reservationId: string) => void
  filter?: ReservationGroupUpsertFilter
  planningInfo?: {
    resourceTypeId: string
    resourceId?: string
  }
}

export type ReservationGroupUpsertGridRowType = {
  resourceTypeId: string
  resourceTypeName: string
  availability: number
  initialAvailability: number
  maxAvailability: number
  notAvailable: boolean
  occupancy: number
  maxOccupancy: number
  maxAdults?: number
  occupancyError: boolean
  initialNotAvailable: boolean
}

export type ResourceTypePerOccupancy = {
  [occupancy: number]: string[]
}

export type ReservationRatesPerOccupancy =
  | {
      [occupancy: number]: ReservationRatesQuery
    }
  | undefined

export type ReservationGroupUpsertGridRowOccupancy = {
  adults: number
  hideChildren: boolean
  children: {
    childId: string
    label: string
    quantity: number
    noOccupancy: boolean
  }[]
}

export type ReservationGroupUpsertGridRowGroupingInfo = {
  resourceTypeId: string
  rateId: string
  chargeCategoryId: string
}

export type ReservationGroupUpsertGridRow = {
  id: string
  groupingInfo: ReservationGroupUpsertGridRowGroupingInfo
  indexInTheGroup: number
  type: ReservationGroupUpsertGridRowType
  resourceNumber: number
  occupancies: ReservationGroupUpsertGridRowOccupancy
  averagePrice: number
  initialAveragePrice: number
  initialTotalPrice: number
  totalPrice: number
  nights: number
  filters: RqQueryForResourceFilter
  position: number // Is the initial price of the row, doesn't change even if the occupancy of the rt changes
  shouldCalculatePrice: boolean // If should calculate price is true you need to call the query to obtain the calculated price
}

export type ReservationGroupUpsertGridTotalRow = {
  id: string
  resources: {
    resourceNumber: number
    occupancy: number
    maxOccupancy: number
  }
  occupancy: {
    adults: number
    children: {
      description: string
      quantity: number
    }[]
  }
  disablePrices: boolean
  averagePrice: number
  initialAveragePrice: number
  initialTotalPrice: number
  totalPrice: number
  nights: number
  position: number
}

export function isReservationGroupUpsertGridTotalRow(value: ReservationGroupUpsertGridRow | ReservationGroupUpsertGridTotalRow | null | undefined): value is ReservationGroupUpsertGridTotalRow {
  if (value === null || value === undefined) {
    return false
  }
  return Object.prototype.hasOwnProperty.call(value, 'resources')
}

export function isReservationGroupUpsertGridRow(value: ReservationGroupUpsertGridRow | ReservationGroupUpsertGridTotalRow | null | undefined): value is ReservationGroupUpsertGridRow {
  if (value === null || value === undefined) {
    return false
  }
  return !isReservationGroupUpsertGridTotalRow(value)
}
